import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/auth";

const DEFAULT_ITEMS = [
  "KHASIM TRENDZ",
  "PREMIUM MENSWEAR",
  "BUTTAIPET • MACHILIPATNAM",
  "BUY ON WHATSAPP",
  "NEW DROPS WEEKLY",
  "STREETWEAR • FORMAL • ACCESSORIES",
];

export default function MarqueeTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.storage.from("images").download("settings/marquee.json");
      if (error) {
        // If file doesn't exist yet, we use default items
        setItems(DEFAULT_ITEMS);
      } else {
        const text = await data.text();
        setItems(JSON.parse(text));
      }
    } catch (e) {
      console.error(e);
      setItems(DEFAULT_ITEMS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (newItems) => {
    setSaving(true);
    try {
      const fileContent = JSON.stringify(newItems);
      const file = new Blob([fileContent], { type: "application/json" });
      
      const { error } = await supabase.storage.from("images").upload("settings/marquee.json", file, {
        upsert: true,
        cacheControl: "0"
      });

      if (error) throw error;
      toast.success("Marquee text updated!");
      setItems(newItems);
    } catch (e) {
      toast.error(formatApiError(e) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = (e) => {
    e.preventDefault();
    const val = newItem.trim();
    if (!val) return;
    const next = [...items, val];
    setNewItem("");
    save(next);
  };

  const handleRemove = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    save(next);
  };

  if (loading) return <div className="py-10 text-center font-display text-2xl">Loading...</div>;

  return (
    <div data-testid="admin-marquee-tab" className="max-w-3xl">
      <div className="mb-6">
        <h2 className="font-display font-black uppercase text-3xl md:text-4xl">Marquee Text</h2>
        <p className="text-black/70">Manage the scrolling text shown at the very top of the website.</p>
      </div>

      <div className="nb-card p-6 bg-white mb-8">
        <form onSubmit={handleAdd} className="flex gap-3 mb-6">
          <input 
            type="text" 
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder="e.g. FLAT 50% OFF TODAY!" 
            className="flex-1 px-4 py-2 border-[3px] border-black rounded-full shadow-[3px_3px_0_0_#0A0A0A] outline-none focus:shadow-[4px_4px_0_0_#FF3366] transition-all"
          />
          <button 
            type="submit" 
            disabled={saving || !newItem.trim()}
            className="nb-btn bg-[var(--primary)] text-white disabled:opacity-50"
          >
            <Plus strokeWidth={3} size={18} className="mr-1 inline" /> Add
          </button>
        </form>

        <div className="flex flex-col gap-3">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 border-[3px] border-black rounded-2xl bg-[var(--secondary)]/10">
              <div className="flex items-center gap-3">
                <GripVertical size={20} className="text-black/30" />
                <span className="font-display font-bold text-lg uppercase tracking-wide">{item}</span>
              </div>
              <button 
                onClick={() => handleRemove(idx)}
                disabled={saving}
                className="w-10 h-10 rounded-full border-[3px] border-black bg-white hover:bg-[#FF3366] hover:text-white flex items-center justify-center transition-colors disabled:opacity-50"
                title="Remove Item"
              >
                <Trash2 size={16} strokeWidth={3} />
              </button>
            </div>
          ))}
          {items.length === 0 && (
            <div className="text-center py-8 text-black/50 font-display">No items in marquee.</div>
          )}
        </div>
      </div>
    </div>
  );
}
